import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import Button from 'shared/Button';
import Icon from 'shared/Icon';
import Text from 'shared/Tipografia/Text';
import TextArea from 'components/TextArea/TextArea';
import theme from 'styles/theme';

import StarRating from './StarRating';
import { useSatisfactionSurvey } from './useSatisfactionSurvey';
import * as S from './styles';

interface ISatisfactionSurveyForm {
  rating: number;
  comentarios: string;
}

const SatisfactionSurvey: React.FC = () => {
  const { visible, sending, submitted, submitError, close, submit } = useSatisfactionSurvey();

  const { control, handleSubmit, watch } = useForm<ISatisfactionSurveyForm>({
    defaultValues: { rating: 0, comentarios: '' },
  });

  const rating = watch('rating');
  const hasRating = rating > 0;

  async function onSubmit(values: ISatisfactionSurveyForm) {
    await submit(values.rating, values.comentarios);
  }

  if (!visible) return null;

  return (
    <S.Card role="dialog" aria-label="Pesquisa de satisfação">
      <S.CloseButton
        type="button"
        aria-label="Fechar pesquisa de satisfação"
        data-testid="satisfactionSurveyClose"
        onClick={close}
      >
        <Icon name="close" width="20" height="20" color={theme.colors.error} strokeWidth={2} />
      </S.CloseButton>

      <Text as="h3" fontWeight="500">
        PESQUISA DE SATISFAÇÃO
      </Text>

      {!submitted && <S.Text>Como você avalia sua experiência geral com o aplicativo RH Digital?</S.Text>}

      <form onSubmit={handleSubmit(onSubmit)}>
        {!submitted && (
          <>
            <Controller
              name="rating"
              control={control}
              render={({ onChange, value }) => <StarRating value={value} onChange={onChange} />}
            />

            {hasRating && (
              <TextArea
                id="satisfactionSurveyComments"
                name="comentarios"
                label="Comentários ou sugestões"
                control={control}
                rows={4}
                maxLength={10000}
                errorRowOnlyWhenInvalid
              />
            )}
          </>
        )}

        <S.Actions>
          {submitError && <S.SubmitError>{submitError}</S.SubmitError>}

          {submitted && <S.Text>Agradecemos a ajuda na construção da nossa plataforma!</S.Text>}

          <S.SubmitButtonWrap $submitted={submitted}>
            <Button
              type="submit"
              id="satisfactionSurveySubmit"
              color={submitted ? theme.colors.success : undefined}
              disabled={!hasRating || sending || submitted}
            >
              {submitted ? (
                <>
                  Enviado
                  <Icon
                    name="check"
                    strokeWidth={3}
                    width="16"
                    height="16"
                    color={theme.colors.white}
                    style={{ marginLeft: '8px' }}
                  />
                </>
              ) : (
                <>{sending ? 'Enviando...' : 'Enviar'}</>
              )}
            </Button>
          </S.SubmitButtonWrap>
        </S.Actions>
      </form>
    </S.Card>
  );
};

export default SatisfactionSurvey;
